(() => {
  'use strict';
  const sidebarMedia = matchMedia('(max-width: 1023px)');
  const syncProductSidebars = () => {
    document.querySelectorAll('[data-product-sidebar-disclosure]').forEach((sidebar) => {
      if (sidebarMedia.matches) sidebar.removeAttribute('open');
      else sidebar.setAttribute('open', '');
    });
  };
  syncProductSidebars();
  sidebarMedia.addEventListener?.('change', syncProductSidebars);
  if (customElements.get('product-detail')) return;
  class ProductDetail extends HTMLElement {
    connectedCallback() {
      if (this.ready) return;
      this.ready = true;
      this.listeners = new AbortController();
      const options = { signal: this.listeners.signal };
      this.addEventListener(
        'change',
        (event) => {
          if (event.target.matches('[data-option-input]')) this.changeVariant(event.target);
        },
        options,
      );
      this.addEventListener(
        'click',
        (event) => {
          const thumb = event.target.closest('[data-select-media]');
          if (thumb) this.selectMedia(thumb.dataset.selectMedia);
          const zoom = event.target.closest('[data-zoom-src]');
          if (zoom) {
            const modal = this.querySelector('.media-lightbox');
            const image = document.createElement('img');
            image.src = zoom.dataset.zoomSrc;
            image.alt = zoom.querySelector('img')?.alt || '';
            modal.querySelector('[data-zoom-image]').replaceChildren(image);
            this.zoomTrigger = zoom;
            modal.showModal();
          }
        },
        options,
      );
      this.querySelector('.media-lightbox')?.addEventListener(
        'close',
        () => this.zoomTrigger?.focus(),
        options,
      );
      this.observeGallery();
      this.selectMedia(this.querySelector('[data-selected-media]')?.dataset.selectedMedia);
      this.initializeModels();
    }
    disconnectedCallback() {
      this.ready = false;
      this.pending?.abort();
      this.listeners?.abort();
      this.observer?.disconnect();
    }
    observeGallery() {
      this.observer?.disconnect();
      const track = this.querySelector('.product-media-track');
      if (!track) return;
      this.observer = new IntersectionObserver(
        (entries) => {
          if (!matchMedia('(max-width: 749px)').matches) return;
          for (const entry of entries)
            if (entry.isIntersecting && entry.intersectionRatio > 0.7)
              this.setPressed(entry.target.dataset.mediaId);
        },
        { root: track, threshold: 0.75 },
      );
      track.querySelectorAll('[data-media-id]').forEach((el) => this.observer.observe(el));
    }
    setPressed(id) {
      this.querySelectorAll('[data-select-media]').forEach((el) =>
        el.setAttribute('aria-pressed', String(el.dataset.selectMedia === id)),
      );
    }
    selectMedia(id) {
      if (!id) return;
      const media = this.querySelector('[data-media-id="' + CSS.escape(id) + '"]');
      if (!media) return;
      this.querySelectorAll('[data-media-id]').forEach((el) => {
        el.classList.toggle('is-selected', el === media);
        if (el !== media) {
          el.querySelectorAll('video').forEach((video) => video.pause());
          el.querySelectorAll('model-viewer').forEach((model) => model.pause?.());
        }
      });
      this.setPressed(id);
      if (matchMedia('(max-width: 749px)').matches) {
        const track = this.querySelector('.product-media-track');
        track.scrollTo({ left: media.offsetLeft - track.firstElementChild.offsetLeft, behavior: 'instant' });
      }
    }
    async changeVariant(input) {
      this.pending?.abort();
      const controller = new AbortController();
      this.pending = controller;
      const focusId = input.id;
      const core = this.querySelector('[data-product-core]');
      const previousQuantity = core.querySelector('input[name=quantity]')?.value;
      const previousPlan = core.querySelector('[name=selling_plan]')?.value;
      const option = input.tagName === 'SELECT' ? input.selectedOptions[0] : input;
      const destination = option.dataset.productUrl || this.dataset.productUrl;
      const url = new URL(destination, location.origin);
      const productBase = url.pathname;
      const values = [...this.querySelectorAll('[data-option-input]:checked,select[data-option-input]')].map(
        (el) => el.value,
      );
      url.searchParams.set('option_values', values.join(','));
      url.searchParams.set('section_id', this.dataset.sectionId);
      core.setAttribute('aria-busy', 'true');
      core.querySelectorAll('[type=submit]').forEach((el) => (el.disabled = true));
      try {
        const response = await fetch(url, { signal: controller.signal });
        if (!response.ok) throw new Error(document.body.dataset.error);
        const html = new DOMParser().parseFromString(await response.text(), 'text/html');
        const next = html.querySelector('product-detail');
        if (!next?.querySelector('[data-product-core]')) throw new Error(document.body.dataset.error);
        // Only the small purchase region is replaced; app blocks and their state remain mounted.
        const isNewProduct = next.dataset.productId !== this.dataset.productId;
        if (isNewProduct) {
          url.searchParams.delete('section_id');
          location.assign(url);
          return;
        }
        core.replaceWith(next.querySelector('[data-product-core]'));
        const quantity = this.querySelector('input[name=quantity]');
        if (quantity && previousQuantity) {
          const min = Number(quantity.min) || 1,
            max = Number(quantity.max) || Infinity,
            step = Number(quantity.step) || 1;
          quantity.value = String(
            Math.max(min, Math.min(max, min + Math.floor((Number(previousQuantity) - min) / step) * step)),
          );
        }
        const plans = this.querySelector('[name=selling_plan]');
        if (plans && [...plans.options].some((el) => el.value === previousPlan)) plans.value = previousPlan;
        const selected = this.querySelector('[data-selected-variant]');
        this.selectMedia(selected?.dataset.selectedMedia);
        const historyURL = new URL(productBase, location.origin);
        if (selected?.dataset.selectedVariant)
          historyURL.searchParams.set('variant', selected.dataset.selectedVariant);
        else historyURL.searchParams.set('option_values', values.join(','));
        history.replaceState({}, '', historyURL);
        this.querySelector('#' + CSS.escape(focusId))?.focus({ preventScroll: true });
        window.Shopify?.PaymentButton?.init?.();
      } catch (error) {
        if (error.name === 'AbortError') return;
        // Keep the selected choices authoritative on failure via a native product navigation.
        url.searchParams.delete('section_id');
        location.assign(url);
      } finally {
        if (!controller.signal.aborted)
          this.querySelector('[data-product-core]')?.removeAttribute('aria-busy');
      }
    }
    initializeModels() {
      if (!this.querySelector('model-viewer') || !window.Shopify?.loadFeatures) return;
      window.Shopify.loadFeatures([
        {
          name: 'model-viewer-ui',
          version: '1.0',
          onLoad: (error) => {
            if (!error && window.Shopify.ModelViewerUI)
              this.querySelectorAll('model-viewer').forEach((el) => new window.Shopify.ModelViewerUI(el));
          },
        },
        {
          name: 'shopify-xr',
          version: '1.0',
          onLoad: (error) => {
            if (error) return;
            const setup = () => {
              const source = this.querySelector('[data-product-models]');
              if (!source || !window.ShopifyXR) return;
              try {
                window.ShopifyXR.addModels(JSON.parse(source.textContent));
                window.ShopifyXR.setupXRElements();
              } catch {
                /* Standard inline model media remains available. */
              }
            };
            if (window.ShopifyXR) setup();
            else
              document.addEventListener('shopify_xr_initialized', setup, {
                once: true,
                signal: this.listeners.signal,
              });
          },
        },
      ]);
    }
  }
  customElements.define('product-detail', ProductDetail);
})();
